import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, FileText, FileCheck, FileX, TrendingUp, Activity } from 'lucide-react';
import { contentStatsService, ContentStats } from '@/services/ContentStatsService';
import { useAutoContentEnhancement } from '@/hooks/useAutoContentEnhancement';
import { formatDistanceToNow } from 'date-fns';

export const ContentEnhancementStats = () => {
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { isRunning, progress } = useAutoContentEnhancement();

  const loadStats = async () => {
    setRefreshing(true);
    const data = await contentStatsService.getContentStats();
    setStats(data);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    loadStats();

    const unsubscribe = contentStatsService.subscribeToUpdates((newStats) => {
      setStats(newStats);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Content Enhancement Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-4 bg-muted animate-pulse rounded" />
            <div className="h-4 bg-muted animate-pulse rounded" />
            <div className="h-4 bg-muted animate-pulse rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const comprehensivePercent = stats.totalModules > 0 
    ? Math.round((stats.comprehensiveModules / stats.totalModules) * 100) 
    : 0;
  const enhancedPercent = stats.totalModules > 0 
    ? Math.round((stats.enhancedModules / stats.totalModules) * 100) 
    : 0;
  const shortPercent = stats.totalModules > 0 
    ? Math.round((stats.shortModules / stats.totalModules) * 100) 
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Content Enhancement Status
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadStats}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Live Server Processing Indicator */}
        {isRunning && progress && (
          <Alert className="border-green-200 bg-green-50">
            <Activity className="h-4 w-4 animate-pulse text-green-600" />
            <AlertDescription className="ml-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-green-900">
                    🔄 Chunk-Based Processing Active
                  </span>
                  <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800">
                    {progress.processedModules}/{progress.totalModules}
                  </Badge>
                </div>
                <div className="text-xs text-green-700">
                  ✅ Success: {progress.successCount} | ❌ Failed: {progress.errorCount}
                </div>
                <div className="text-xs text-green-800 font-medium">
                  🚀 Self-perpetuating chunks - processes all {progress.totalModules} modules automatically
                </div>
                <div className="text-xs text-green-600">
                  💾 Safe to close browser - runs independently on server
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Total Modules</p>
            <p className="text-2xl font-bold">{stats.totalModules}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Avg Words</p>
            <p className="text-2xl font-bold">{stats.avgWordCount}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Comprehensive</p>
            <p className="text-2xl font-bold text-green-600">{stats.comprehensiveModules}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Enhanced</p>
            <p className="text-2xl font-bold text-blue-600">{stats.enhancedModules}</p>
          </div>
        </div>

        {/* Comprehensive Modules */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium">Comprehensive (≥2000 words)</span>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              {stats.comprehensiveModules} ({comprehensivePercent}%)
            </Badge>
          </div>
          <Progress value={comprehensivePercent} className="h-2" />
        </div>

        {/* Enhanced Modules */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Enhanced (1000-1999 words)</span>
            </div>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              {stats.enhancedModules} ({enhancedPercent}%)
            </Badge>
          </div>
          <Progress value={enhancedPercent} className="h-2" />
        </div>

        {/* Short Modules */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileX className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium">Short (&lt;1000 words)</span>
            </div>
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              {stats.shortModules} ({shortPercent}%)
            </Badge>
          </div>
          <Progress value={shortPercent} className="h-2" />
        </div>

        {/* Last Update */}
        {stats.lastUpdate && (
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              Last updated {formatDistanceToNow(new Date(stats.lastUpdate), { addSuffix: true })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
